import isEqual from 'lodash/isEqual';
import FuzzySearch from 'fuzzy-search';
import orderBy from 'lodash/orderBy';
import uniq from 'lodash/uniq';
import intersection from 'lodash/intersection';
const filterKeys = ['area', 'type', 'employees', 'rental', 'search'];

export default (properties = [], filter, state, defaultState) => {
  const selected = {};
  const areaLookup = {};
  const typeLookup = {};
  const validAreas = filter.area.options.map(({ id }) => id);

  filter.area.options.forEach(({ id, title }) => {
    areaLookup[id] = title;
  });

  filter.type.options.forEach(({ id, title }) => {
    typeLookup[id] = title;
  });

  Object.keys(state)
    .filter(key => filterKeys.indexOf(key) !== -1)
    .forEach(key => {
      if (!isEqual(state[key], defaultState[key])) {
        selected[key] = state[key];
      }
    });

  let filtered = properties
    .map(({ premises, ...rest }) => {
      const filteredPremises = (premises || []).filter(
        ({ type, employees, rental }) => {
          if (selected.type) {
            if (selected.type !== type) {
              return false;
            }
          }

          if (selected.employees) {
            if (!employees) {
              return false;
            }

            if (employees.from < selected.employees[0]) {
              return false;
            }

            if (employees.from > selected.employees[1]) {
              return false;
            }

            // dont filter element if employees range end is at max
            if (selected.employees[1] !== filter.employees.max) {
              if (selected.employees[1] > employees.to) {
                return false;
              }
            }
          }

          if (selected.rental) {
            if (rental < selected.rental[0]) {
              return false;
            }

            // dont filter element if rental range end is at max
            if (selected.rental[1] !== filter.rental.max) {
              if (rental > selected.rental[1]) {
                return false;
              }
            }
          }

          return true;
        }
      );
      return {
        ...rest,
        premises: filteredPremises,
      };
    })
    .filter(property => {
      let match = true;

      if (selected.area) {
        const cleanedSelectedAreas = selected.area.filter(
          id => validAreas.indexOf(id) > -1
        );

        if (
          cleanedSelectedAreas.length &&
          intersection(property.areas, cleanedSelectedAreas).length === 0
        ) {
          match = false;
        }
      }

      if (selected.type || selected.employees || selected.rental) {
        if (!property.premises.length) {
          match = false;
        }
      }

      return match;
    })
    .map(({ areas = [], premises = [], ...props }) => ({
      ...props,
      premises,
      areas: areas.map(key => areaLookup[key]),
      type: (() => {
        if (state.display === 'list') {
          const types = premises
            .map(({ type }) => typeLookup[type])
            .filter(type => type);

          return uniq(types).join(' / ');
        }
        if (state.type) {
          const typeString = typeLookup[state.type];
          if (typeString) {
            return `${typeString} (${premises.length})`;
          }
        }
        return `${filter.labels.premises} (${premises.length})`;
      })(),
      vacancy: (() => {
        if (premises.length) {
          return premises[0].vacantFrom;
        }
        return '';
      })(),
    }));

  if (selected.search) {
    const searcher = new FuzzySearch(
      filtered,
      ['name', 'landlord', 'intro', 'areas'],
      {
        sort: true,
      }
    );
    filtered = searcher.search(state.search);
  }

  if (state.order === 'size' && state.display === 'list') {
    filtered = filtered.sort((a, b) =>
      state.desc ? a.size - b.size : b.size - a.size
    );
  }

  if (state.order === 'area' && state.display === 'list') {
    filtered = filtered.sort((a, b) =>
      Array.isArray(a.areas) && Array.isArray(b.areas) && state.desc
        ? a.areas[0].localeCompare(b.areas[0])
        : b.areas[0].localeCompare(a.areas[0])
    );
  }

  if (
    state.order !== 'area' &&
    state.order !== 'size' &&
    state.display === 'list'
  ) {
    const order = state.desc ? ['desc'] : ['asc'];
    filtered = orderBy(filtered, [state.order], order);
  }

  return filtered;
};
